import React from "react";
import { CeleryTaskStatus, ParentTask, UNREADY_STATES } from "./types";
import { Spinner } from "../../utils/Spinner";
import { TaskStepProgressbar } from "./TaskStepProgressbar";

type BGColorMap = Record<CeleryTaskStatus | "FATAL_ERROR", string>;

export const summaryBGColorMap: BGColorMap = {
  PENDING: "!tw-bg-blue-500 !tw-text-white",
  RECEIVED: "!tw-bg-gray-500 !tw-text-white",
  STARTED: "!tw-bg-blue-500 !tw-text-white",
  SUCCESS: "!tw-bg-green-500 !tw-text-white",
  FAILURE: "!tw-bg-red-500 !tw-text-white",
  REVOKED: "!tw-bg-gray-500 !tw-text-white",
  REJECTED: "!tw-bg-gray-500 !tw-text-white",
  RETRY: "!tw-bg-gray-500 !tw-text-white",
  FATAL_ERROR: "!tw-bg-gray-500 !tw-text-white",
};

type Props = {
  task: ParentTask;
  isPlaceholder?: boolean;
};

export const TaskProgressbar: React.FC<Props> = ({ task, isPlaceholder }) => {
  const progress = task?.progress;
  const stepProgress = task?.step_progress;
  if (!progress)
    return <div className="tw-animate-pulse">Checking progress...</div>;
  const isHardError = task.status === "FAILURE" && !!task.result?.error;
  const totalDone = Math.min(progress.success + progress.error, progress.total);

  const allIsDone = totalDone === progress.total;

  const calcBase = Math.round((totalDone / progress.total) * 100);

  const calcProgressForProgressbar = Math.max(calcBase, 0);

  const progressTotalPercentage =
    progress && progress.total > 0
      ? `${Math.min(calcProgressForProgressbar, 100)}%`
      : "0%";
  const displayProgress =
    progress && progress.total > 0 ? `${Math.min(calcBase, 100)}%` : "0%";

  const inProgress =
    UNREADY_STATES.includes(task.status_from_result) || !allIsDone;

  const bgColor =
    summaryBGColorMap[allIsDone ? task.status_from_result : "PENDING"];

  return (
    <div>
      {task?.result?.error && (
        <small className="tw-text-red-500">
          <b>ERROR:</b> <pre>{task?.result?.error}</pre>
        </small>
      )}
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-w-full tw-bg-gray-200 tw-rounded-md">
          <div
            className={`tw-font-medium tw-flex tw-justify-end tw-items-center tw-gap-2 tw-p-0.5 tw-px-3 tw-rounded-md ${bgColor} tw-overflow-hidden tw-transition-all tw-duration-500 tw-ease-out tw-min-w-max`}
            style={{
              width: isHardError ? "100%" : progressTotalPercentage,
            }}
          >
            {inProgress && <Spinner size="mini" />} {displayProgress}
          </div>
        </div>
      </div>
      <small className="tw-text-gray-500 tw-text-xs">
        {isPlaceholder
          ? "Loading..."
          : `${totalDone} / ${progress.total} with ${progress.error} Failure(s) & ${progress.success} Success(es)`}
      </small>
      {stepProgress && (
        <div className="tw-flex tw-gap-4 !tw-mt-8 tw-flex-wrap">
          {Object.entries(stepProgress).map(([step, progress]) => (
            <TaskStepProgressbar
              key={step}
              step={step}
              completed={progress.completed}
              total={progress.total}
            />
          ))}
        </div>
      )}
    </div>
  );
};
